import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import Aviso from './Aviso';
import { registrarDevolucao, type ItemDaVenda } from '../lib/consultas';
import { brl, paraNumero, qtd } from '../lib/formato';
import { nomeDoItemVendido } from '../lib/coringa';
import { mensagemDeErro } from '../lib/supabase';

/**
 * O painel de troca/estorno/devolução (033), com o abatimento do fiado da 065.
 *
 * MORAVA DENTRO DE Vendas.tsx até 28/08/2026. Saiu de lá porque o dono
 * reportou o defeito que importa: "a devolução de peças no fiado não está
 * funcionando" — e não estava, no sentido que conta. O motor sempre esteve
 * certo (a peça volta ao estoque e abate a dívida); o que não existia era
 * COMO CHEGAR nele. A devolução só abria pela aba Vendas, e a aba Vendas abre
 * filtrada nos últimos 7 dias: no dia do relato, 9 dos 36 fiados em aberto
 * eram mais velhos que isso e a venda nem aparecia na lista.
 *
 * Agora o mesmo painel serve duas portas — a venda (aba Vendas) e a dívida
 * (aba Fiado). UMA implementação, porque duas divergiriam na primeira
 * correção, e devolução é onde estoque, caixa, vale e dívida se cruzam.
 *
 * O DESTINO DO VALOR, na ordem em que o banco resolve (registrar_devolucao):
 *
 *   1. Se a venda tem fiado EM ABERTO, o valor devolvido abate a dívida
 *      primeiro. Isso não é escolha da tela: entregar vale ou dinheiro a quem
 *      está devendo é emprestar de novo para o mesmo devedor.
 *   2. O que sobrar segue o modo escolhido aqui — VALE (crédito "a ver" para
 *      a próxima compra) ou ESTORNO (dinheiro sai do caixa agora).
 *
 * A TROCA por outra peça se faz em dois passos, e é de propósito: devolve
 * gerando vale, e a peça nova sai numa venda nova resgatando o vale. A
 * diferença se acerta sozinha — se a nova é mais cara o cliente paga o resto,
 * se é mais barata a sobra fica no vale (ou o gerente abona, migração 034).
 */
export default function PainelDevolucao({
  saleId, itens, jaDevolvido, aoFechar, aoRegistrar,
  contexto = 'venda', iniciarComTudo = false,
}: {
  saleId: string;
  itens: ItemDaVenda[];
  jaDevolvido: Record<string, number>;
  aoFechar: () => void;
  aoRegistrar: () => void;
  /** Muda só o texto: na aba Fiado o abatimento da dívida é o assunto. */
  contexto?: 'venda' | 'fiado';
  /** Abre com TUDO já marcado — é o atalho "virar a ver" da aba Vendas. */
  iniciarComTudo?: boolean;
}) {
  const disponivelDe = (i: ItemDaVenda) =>
    Number(i.quantidade) - (jaDevolvido[i.id] ?? 0);

  const [qtds, setQtds] = useState<Record<string, string>>(() => (
    iniciarComTudo
      ? Object.fromEntries(itens
          .filter((i) => disponivelDe(i) > 0)
          .map((i) => [i.id, String(disponivelDe(i))]))
      : {}
  ));
  const [modo, setModo] = useState<'vale' | 'estorno'>('vale');
  const [forma, setForma] = useState<'dinheiro' | 'pix' | 'debito' | 'credito' | 'transferencia'>('dinheiro');
  const [motivoDev, setMotivoDev] = useState('');
  const [vale, setVale] = useState<string | null>(null);
  // Quanto a devolução abateu do fiado (065) — o gerente precisa saber que o
  // dinheiro NÃO saiu da gaveta, saiu da dívida.
  const [abatido, setAbatido] = useState<number | null>(null);

  const linhas = itens.map((i) => ({ ...i, disponivel: disponivelDe(i) }));

  const escolhidos = linhas
    .map((l) => ({ sale_item_id: l.id, quantidade: paraNumero(qtds[l.id] ?? '') ?? 0, preco: Number(l.preco_unit) }))
    .filter((e) => e.quantidade > 0);
  const valor = escolhidos.reduce((soma, e) => soma + Math.round(e.quantidade * e.preco * 100) / 100, 0);
  const estourou = linhas.some((l) => (paraNumero(qtds[l.id] ?? '') ?? 0) > l.disponivel);
  const temSobrando = linhas.some((l) => l.disponivel > 0);

  const marcarTudo = () => setQtds(Object.fromEntries(
    linhas.filter((l) => l.disponivel > 0).map((l) => [l.id, String(l.disponivel)]),
  ));

  const registrar = useMutation({
    mutationFn: () => registrarDevolucao({
      saleId,
      itens: escolhidos.map(({ sale_item_id, quantidade }) => ({ sale_item_id, quantidade })),
      modo,
      motivo: motivoDev,
      forma: modo === 'estorno' ? forma : null,
    }),
    onSuccess: (r) => {
      const noFiado = Number(r.abatido_no_fiado ?? 0);
      if (noFiado > 0) setAbatido(noFiado);
      if (r.codigo) setVale(r.codigo);
      else if (noFiado <= 0) aoRegistrar();
    },
  });

  // Troca concluída com vale, ou devolução que abateu fiado: mostra o desfecho
  // ANTES de fechar. O código do vale é o que o gerente anota/imprime; o
  // abatimento é o que explica por que a gaveta não mexeu.
  if (vale || abatido !== null) {
    return (
      <div className="mt-3 space-y-2 border border-ok/40 bg-ok/5 p-3">
        {abatido !== null && (
          <p className="text-sm">
            Devolução registrada. <strong>{brl(abatido)}</strong> saíram da dívida
            do cliente — quem devia e devolveu a peça deixa de dever aquele valor.
            Nada saiu do caixa por essa parte.
          </p>
        )}
        {vale && (
          <p className="text-sm">
            {abatido !== null ? 'O que sobrou virou o vale ' : (
              'Devolução registrada. O crédito da troca é o vale '
            )}
            <strong className="font-mono">{vale}</strong> — use-o na venda nova
            (campo Vale "a ver"). A 2ª via imprime na aba Notas "a ver".
          </p>
        )}
        <button type="button" className="botao-secundario w-full py-2 text-sm" onClick={aoRegistrar}>
          Entendi
        </button>
      </div>
    );
  }

  return (
    <div className="mt-3 space-y-3 border border-signal/40 bg-signal/5 p-3">
      <p className="text-xs text-mute">
        {contexto === 'fiado'
          ? 'Escolha o que está voltando. O estoque volta na hora, e o valor sai da dívida deste cliente — só o que passar do que ele deve é que vira vale ou dinheiro.'
          : 'Escolha o que está voltando. O estoque volta na hora; a venda original fica registrada como está. Se a venda tiver fiado em aberto, o valor abate a dívida antes de virar qualquer outra coisa.'}
      </p>

      <div className="flex items-center justify-between">
        <p className="rotulo">Peças da venda</p>
        {temSobrando && (
          <button type="button" className="text-xs text-dim underline" onClick={marcarTudo}>
            devolver tudo
          </button>
        )}
      </div>

      <ul className="space-y-2">
        {linhas.map((l) => (
          <li key={l.id} className="flex items-center justify-between gap-2 text-sm">
            <span className="min-w-0 truncate">
              {nomeDoItemVendido(l)}
              <span className="ml-1 text-xs text-dim">
                (vendidas {qtd(Number(l.quantidade))}
                {(jaDevolvido[l.id] ?? 0) > 0 ? `, já devolvidas ${qtd(jaDevolvido[l.id]!)}` : ''})
              </span>
            </span>
            {l.disponivel > 0 ? (
              <input
                className="campo w-20 py-1 text-right"
                inputMode="decimal"
                placeholder="0"
                value={qtds[l.id] ?? ''}
                onChange={(e) => setQtds((a) => ({ ...a, [l.id]: e.target.value }))}
              />
            ) : (
              <span className="shrink-0 text-xs text-dim">tudo devolvido</span>
            )}
          </li>
        ))}
      </ul>

      <div>
        {/* O rótulo diz "se sobrar" nos DOIS contextos porque a aba Vendas
            também lista venda fiada: prometer ali que o valor vira vale seria
            mentira sempre que a venda tiver dívida aberta — quem decide isso
            é o banco, e a dívida vem primeiro. */}
        <p className="rotulo">Se sobrar depois de abater a dívida</p>
        <div className="grid grid-cols-2 gap-2">
          <button type="button" onClick={() => setModo('vale')}
                  className={`border px-3 py-2 text-left text-xs ${modo === 'vale' ? 'border-signal bg-signal/10' : 'border-edge'}`}>
            <span className="block font-bold">Vale para troca</span>
            <span className="block text-mute">o cliente usa na nova compra</span>
          </button>
          <button type="button" onClick={() => setModo('estorno')}
                  className={`border px-3 py-2 text-left text-xs ${modo === 'estorno' ? 'border-signal bg-signal/10' : 'border-edge'}`}>
            <span className="block font-bold">Estornar</span>
            <span className="block text-mute">o dinheiro sai do caixa</span>
          </button>
        </div>
      </div>

      {modo === 'estorno' && (
        <div>
          <label className="rotulo" htmlFor={`forma-estorno-${saleId}`}>Por onde o dinheiro sai</label>
          <select id={`forma-estorno-${saleId}`} className="campo" value={forma}
                  onChange={(e) => setForma(e.target.value as typeof forma)}>
            <option value="dinheiro">Dinheiro</option>
            <option value="pix">PIX</option>
            <option value="debito">Débito</option>
            <option value="credito">Crédito</option>
            <option value="transferencia">Transferência</option>
          </select>
        </div>
      )}

      <input
        className="campo py-2 text-sm"
        value={motivoDev}
        placeholder="Motivo (obrigatório): defeito, tamanho errado…"
        onChange={(e) => setMotivoDev(e.target.value)}
      />

      {estourou && (
        <Aviso tipo="erro">Há quantidade acima do que ainda pode ser devolvido.</Aviso>
      )}
      {registrar.error ? <Aviso tipo="erro">{mensagemDeErro(registrar.error)}</Aviso> : null}

      <div className="flex gap-2">
        <button type="button" className="botao-secundario flex-1 py-2 text-sm" onClick={aoFechar}>
          Voltar
        </button>
        <button type="button"
                className="botao-primario flex-1 py-2 text-sm disabled:opacity-50"
                disabled={registrar.isPending || escolhidos.length === 0 || estourou || !motivoDev.trim()}
                onClick={() => registrar.mutate()}>
          {registrar.isPending
            ? 'Registrando…'
            : `Devolver ${valor > 0 ? brl(valor) : ''}`}
        </button>
      </div>
    </div>
  );
}
